import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/api';
import { toast } from 'sonner';
import {
  Building2, MapPin, Phone, Mail, Globe, Users, FolderOpen,
  FileQuestion, FileText, AlertTriangle, Bell, Receipt, Calendar,
  PenTool, Clock, TrendingUp, ArrowRight, Plus, Edit2, Trash2,
  DollarSign, ChevronRight, RefreshCw, BarChart3, PieChart, Download
} from 'lucide-react';

// Simple Bar Chart Component
const SimpleBarChart = ({ data, dataKey, nameKey, color = '#3b82f6', height = 200 }) => {
  const maxValue = Math.max(...data.map(d => d[dataKey] || 0), 1);
  
  return (
    <div className="w-full" style={{ height }}>
      <div className="flex items-end justify-between h-full gap-2 pb-8 relative">
        {data.map((item, index) => (
          <div key={index} className="flex flex-col items-center flex-1">
            <div 
              className="w-full rounded-t transition-all hover:opacity-80"
              style={{ 
                height: `${(item[dataKey] / maxValue) * (height - 40)}px`,
                backgroundColor: color,
                minHeight: item[dataKey] > 0 ? '4px' : '0'
              }}
            />
            <span className="text-xs text-slate-500 mt-2 absolute bottom-0 transform -rotate-45 origin-top-left whitespace-nowrap">
              {item[nameKey]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Donut Chart Component
const DonutChart = ({ data, colors, size = 150 }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = 0;
  
  const segments = data.map((item, index) => {
    const angle = total > 0 ? (item.value / total) * 360 : 0;
    const startAngle = currentAngle;
    currentAngle += angle;
    
    const x1 = 50 + 40 * Math.cos((startAngle - 90) * Math.PI / 180);
    const y1 = 50 + 40 * Math.sin((startAngle - 90) * Math.PI / 180);
    const x2 = 50 + 40 * Math.cos((startAngle + angle - 90) * Math.PI / 180);
    const y2 = 50 + 40 * Math.sin((startAngle + angle - 90) * Math.PI / 180);
    
    const largeArc = angle > 180 ? 1 : 0;
    
    return {
      ...item,
      path: `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`,
      color: colors[index % colors.length]
    };
  });

  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
        {segments.map((seg, i) => (
          <path key={i} d={seg.path} fill={seg.color} className="hover:opacity-80 transition-opacity" />
        ))}
        <circle cx="50" cy="50" r="25" fill="white" />
        <text x="50" y="50" textAnchor="middle" dominantBaseline="middle" className="text-lg font-bold fill-slate-700">
          {total}
        </text>
      </svg>
      <div className="space-y-1">
        {data.map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[i % colors.length] }} />
            <span className="text-slate-600">{item.name}: {item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// KPI Card Component
const KPICard = ({ title, stats, icon: Icon, color, onClick }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 hover:border-blue-400',
    green: 'bg-green-50 border-green-200 hover:border-green-400',
    amber: 'bg-amber-50 border-amber-200 hover:border-amber-400',
    red: 'bg-red-50 border-red-200 hover:border-red-400',
    purple: 'bg-purple-50 border-purple-200 hover:border-purple-400',
    slate: 'bg-slate-50 border-slate-200 hover:border-slate-400',
    cyan: 'bg-cyan-50 border-cyan-200 hover:border-cyan-400',
  };

  const iconColors = {
    blue: 'text-blue-500',
    green: 'text-green-500',
    amber: 'text-amber-500',
    red: 'text-red-500',
    purple: 'text-purple-500',
    slate: 'text-slate-500',
    cyan: 'text-cyan-500',
  };

  return (
    <div 
      onClick={onClick}
      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${colorClasses[color]} ${onClick ? 'hover:shadow-md' : ''}`}
    >
      <div className="flex justify-between items-start mb-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{title}</p>
        <Icon className={`h-5 w-5 ${iconColors[color]}`} />
      </div>
      <p className="text-3xl font-bold text-slate-900">{stats.total || 0}</p>
      <div className="flex gap-2 mt-2 flex-wrap">
        {stats.open !== undefined && <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700">Open: {stats.open}</Badge>}
        {stats.overdue !== undefined && stats.overdue > 0 && <Badge variant="outline" className="text-xs bg-red-100 text-red-700">Overdue: {stats.overdue}</Badge>}
        {stats.pending !== undefined && <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700">Pending: {stats.pending}</Badge>}
      </div>
    </div>
  );
};

// Project Card Component
const ProjectCard = ({ project, onClick }) => (
  <div 
    onClick={onClick}
    className="bg-white border border-slate-200 rounded-lg p-4 cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all group"
  >
    <div className="flex justify-between items-start mb-3">
      <div>
        <h4 className="font-semibold text-slate-900">{project.name}</h4>
        <p className="text-sm text-slate-500">{project.contract_type}</p>
      </div>
      <Badge variant={project.status === 'active' ? 'default' : 'secondary'}>
        {project.status}
      </Badge>
    </div>
    <div className="grid grid-cols-4 gap-2 text-center">
      <div className="p-2 bg-slate-50 rounded">
        <p className="text-lg font-bold text-blue-600">{project.rfis}</p>
        <p className="text-xs text-slate-500">RFIs</p>
      </div>
      <div className="p-2 bg-slate-50 rounded">
        <p className="text-lg font-bold text-amber-600">{project.site_instructions}</p>
        <p className="text-xs text-slate-500">SIs</p>
      </div>
      <div className="p-2 bg-slate-50 rounded">
        <p className="text-lg font-bold text-red-600">{project.delay_events}</p>
        <p className="text-xs text-slate-500">Events</p>
      </div>
      <div className="p-2 bg-slate-50 rounded">
        <p className="text-lg font-bold text-purple-600">{project.notices}</p>
        <p className="text-xs text-slate-500">Notices</p>
      </div>
    </div>
    <div className="flex items-center justify-end mt-3 text-sm text-slate-500 group-hover:text-slate-700">
      View Details <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
    </div>
  </div>
);

// Branch Form Dialog
const BranchFormDialog = ({ open, onOpenChange, clientId, branch, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    is_head_office: false,
    billing_email: '',
    billing_contact: '',
    phone: '',
    physical_address: { street: '', city: '', province: '', postal_code: '', country: 'South Africa' }
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (branch) {
      setFormData({
        name: branch.name || '',
        is_head_office: branch.is_head_office || false,
        billing_email: branch.billing_email || '',
        billing_contact: branch.billing_contact || '',
        phone: branch.phone || '',
        physical_address: branch.physical_address || { street: '', city: '', province: '', postal_code: '', country: 'South Africa' }
      });
    } else {
      setFormData({
        name: '',
        is_head_office: false,
        billing_email: '',
        billing_contact: '',
        phone: '',
        physical_address: { street: '', city: '', province: '', postal_code: '', country: 'South Africa' }
      });
    }
  }, [branch, open]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Branch name is required');
      return;
    }
    setSaving(true);
    try {
      if (branch) {
        await api.clients.updateBranch(clientId, branch.id, formData);
        toast.success('Branch updated');
      } else {
        await api.clients.addBranch(clientId, formData);
        toast.success('Branch added');
      }
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to save branch');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{branch ? 'Edit Branch' : 'Add Branch'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Branch Name *</Label>
            <Input 
              value={formData.name} 
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., Johannesburg Office"
            />
          </div>
          <div className="flex items-center gap-2">
            <input 
              type="checkbox" 
              checked={formData.is_head_office}
              onChange={(e) => setFormData({...formData, is_head_office: e.target.checked})}
              className="rounded"
            />
            <Label>Head Office</Label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Billing Email</Label>
              <Input 
                value={formData.billing_email} 
                onChange={(e) => setFormData({...formData, billing_email: e.target.value})}
              />
            </div>
            <div>
              <Label>Billing Contact</Label>
              <Input 
                value={formData.billing_contact} 
                onChange={(e) => setFormData({...formData, billing_contact: e.target.value})}
              />
            </div>
          </div>
          <div>
            <Label>Phone</Label>
            <Input 
              value={formData.phone} 
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
            />
          </div>
          <div>
            <Label>Address</Label>
            <Input 
              value={formData.physical_address?.street || ''} 
              onChange={(e) => setFormData({...formData, physical_address: {...formData.physical_address, street: e.target.value}})}
              placeholder="Street Address"
              className="mb-2"
            />
            <div className="grid grid-cols-2 gap-2">
              <Input 
                value={formData.physical_address?.city || ''} 
                onChange={(e) => setFormData({...formData, physical_address: {...formData.physical_address, city: e.target.value}})}
                placeholder="City"
              />
              <Input 
                value={formData.physical_address?.province || ''} 
                onChange={(e) => setFormData({...formData, physical_address: {...formData.physical_address, province: e.target.value}})}
                placeholder="Province"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Branch'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function ClientDashboard() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.clients.getDashboard(clientId, 30);
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load client dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      setDocumentsLoading(true);
      const response = await api.get('/documents/generated');
      // Filter documents for this client's projects
      const clientProjectIds = dashboard?.project_breakdown?.map(p => p.id) || [];
      const clientDocs = response.data.filter(doc => clientProjectIds.includes(doc.project_id));
      setDocuments(clientDocs);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [clientId]);

  useEffect(() => {
    if (activeTab === 'documents' && dashboard) {
      loadDocuments();
    }
  }, [activeTab, dashboard]);

  const handleDownloadDocument = async (docId, format) => {
    try {
      const response = await api.get(`/documents/generated/${docId}/download/${format}`, {
        responseType: 'blob'
      });
      
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const err = JSON.parse(text);
        toast.error(err.detail || `Failed to download ${format.toUpperCase()}`);
        return;
      }

      const contentDisposition = response.headers?.['content-disposition'];
      let filename = `document.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const detail = error.response?.data;
      let msg = `Failed to download ${format.toUpperCase()}`;
      if (detail instanceof Blob) {
        try {
          const text = await detail.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch {}
      } else if (typeof detail === 'object' && detail?.detail) {
        msg = detail.detail;
      }
      toast.error(msg);
    }
  };

  const handleDeleteBranch = async (branchId) => {
    if (!window.confirm('Are you sure you want to delete this branch?')) return;
    try {
      await api.clients.deleteBranch(clientId, branchId);
      toast.success('Branch deleted');
      loadDashboard();
    } catch (error) {
      toast.error('Failed to delete branch');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (!dashboard) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Client not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const { client, stats, project_breakdown, upcoming_deadlines, billing_summary, billing_by_branch, monthly_activity, recent_activity } = dashboard;

  // Prepare chart data
  const chartColors = ['#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981', '#06b6d4'];
  
  const rfiChartData = [
    { name: 'Open', value: stats.rfis?.open || 0 },
    { name: 'Closed', value: stats.rfis?.closed || 0 },
    { name: 'Overdue', value: stats.rfis?.overdue || 0 },
  ];

  const eventChartData = [
    { name: 'Active', value: stats.delay_events?.active || 0 },
    { name: 'Notice Pending', value: stats.delay_events?.notice_pending || 0 },
    { name: 'Closed', value: stats.delay_events?.closed || 0 },
  ];

  const noticeChartData = [
    { name: 'Draft', value: stats.notices?.draft || 0 },
    { name: 'Submitted', value: stats.notices?.submitted || 0 },
    { name: 'Acknowledged', value: stats.notices?.acknowledged || 0 },
  ];

  const claimChartData = [
    { name: 'Draft', value: stats.claims?.draft || 0 },
    { name: 'Under Review', value: stats.claims?.under_review || 0 },
    { name: 'Approved', value: stats.claims?.approved || 0 },
    { name: 'Rejected', value: stats.claims?.rejected || 0 },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden" data-testid="client-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-3xl font-barlow font-bold uppercase tracking-tight truncate">{client.name}</h1>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-slate-500">
              {client.email && (
                <span className="flex items-center gap-1">
                  <Mail className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" /> 
                  <span className="truncate max-w-[150px] sm:max-w-none">{client.email}</span>
                </span>
              )}
              {client.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" /> {client.phone}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={loadDashboard}>
              <RefreshCw className="h-4 w-4" />
              <span className="hidden sm:inline ml-2">Refresh</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(`/clients/${clientId}/edit`)}>
              <Edit2 className="h-4 w-4" />
              <span className="hidden sm:inline ml-2">Edit</span>
            </Button>
          </div>
        </div>

        {/* Summary Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FolderOpen className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_projects}</p>
                  <p className="text-sm text-slate-500">Total Projects</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active_projects}</p>
                  <p className="text-sm text-slate-500">Active Projects</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{upcoming_deadlines?.length || 0}</p>
                  <p className="text-sm text-slate-500">Upcoming Deadlines</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">R{(billing_summary?.outstanding || 0).toLocaleString()}</p>
                  <p className="text-sm text-slate-500">Outstanding</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Mobile: Dropdown */}
          <div className="sm:hidden mb-4">
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select view" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="overview">Overview</SelectItem>
                <SelectItem value="projects">Projects ({stats.total_projects})</SelectItem>
                <SelectItem value="documents">Documents</SelectItem>
                <SelectItem value="analytics">Analytics</SelectItem>
                <SelectItem value="deadlines">Deadlines</SelectItem>
                <SelectItem value="billing">Billing</SelectItem>
                <SelectItem value="branches">Branches</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Desktop: Tab List */}
          <TabsList className="bg-slate-100 hidden sm:inline-flex">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="projects">Projects ({stats.total_projects})</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="deadlines">Deadlines</TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
            <TabsTrigger value="branches">Branches</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Module KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
              <KPICard title="RFIs" stats={stats.rfis || {}} icon={FileQuestion} color="blue" />
              <KPICard title="Site Instructions" stats={stats.site_instructions || {}} icon={FileText} color="amber" />
              <KPICard title="Drawings" stats={stats.drawings || {}} icon={PenTool} color="cyan" />
              <KPICard title="Delay Events" stats={stats.delay_events || {}} icon={AlertTriangle} color="red" />
              <KPICard title="Notices" stats={stats.notices || {}} icon={Bell} color="purple" />
              <KPICard title="Claims" stats={stats.claims || {}} icon={Receipt} color="slate" />
              <KPICard title="Programs" stats={stats.programs || {}} icon={Calendar} color="green" />
              <KPICard 
                title="Branches" 
                stats={{ total: client.branches?.length || 0 }} 
                icon={Building2} 
                color="blue"
                onClick={() => setActiveTab('branches')}
              />
            </div>

            {/* Activity & Deadlines */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {recent_activity?.length > 0 ? recent_activity.map((item, i) => (
                      <div 
                        key={i} 
                        className="flex items-center justify-between p-2 bg-slate-50 rounded cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => {
                          // Navigate based on item type
                          const typeRoutes = {
                            'SI': `/si/${item.id}`,
                            'Site Instruction': `/si/${item.id}`,
                            'RFI': `/projects/${item.project_id}/rfi/${item.id}`,
                            'Drawing': `/projects/${item.project_id}/drawings/${item.id}`,
                            'Delay Event': `/events/${item.id}`,
                            'Notice': `/notices/${item.id}`,
                            'Claim': `/claims/${item.id}`
                          };
                          const route = typeRoutes[item.type] || `/projects/${item.project_id}`;
                          navigate(route);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">{item.type}</Badge>
                          <div>
                            <p className="font-medium text-sm">{item.reference}</p>
                            <p className="text-xs text-slate-500">{item.title}</p>
                          </div>
                        </div>
                        <Badge variant={item.status === 'open' ? 'default' : 'secondary'}>
                          {item.status}
                        </Badge>
                      </div>
                    )) : (
                      <p className="text-center text-slate-400 py-4">No recent activity</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Upcoming Deadlines */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-500" />
                    Upcoming Deadlines
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {upcoming_deadlines?.length > 0 ? upcoming_deadlines.slice(0, 10).map((item, i) => (
                      <div 
                        key={i} 
                        className="flex items-center justify-between p-2 bg-slate-50 rounded cursor-pointer hover:bg-slate-100"
                        onClick={() => navigate(`/projects/${item.project_id}`)}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">{item.type}</Badge>
                            <span className="font-medium text-sm">{item.reference}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{item.project_name}</p>
                        </div>
                        <Badge className={item.days_remaining <= 2 ? 'bg-red-500' : 'bg-amber-500'}>
                          {item.days_remaining}d
                        </Badge>
                      </div>
                    )) : (
                      <p className="text-center text-slate-400 py-4">No upcoming deadlines</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {project_breakdown?.map((project) => (
                <ProjectCard 
                  key={project.id} 
                  project={project}
                  onClick={() => navigate(`/projects/${project.id}`)}
                />
              ))}
            </div>
            {(!project_breakdown || project_breakdown.length === 0) && (
              <div className="text-center py-12 bg-slate-50 rounded-lg">
                <FolderOpen className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No projects found for this client</p>
              </div>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {/* Monthly Activity Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Monthly Activity (Last 6 Months)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {monthly_activity && monthly_activity.length > 0 ? (
                  <div className="h-64">
                    <div className="flex items-end justify-between h-48 gap-4 pb-8 relative border-b border-l border-slate-200">
                      {monthly_activity.map((month, index) => {
                        const total = month.rfis + month.site_instructions + month.delay_events + month.notices + month.claims;
                        const maxTotal = Math.max(...monthly_activity.map(m => m.rfis + m.site_instructions + m.delay_events + m.notices + m.claims), 1);
                        return (
                          <div key={index} className="flex flex-col items-center flex-1 group">
                            <div className="flex flex-col w-full gap-0.5">
                              <div className="bg-blue-500 w-full rounded-t" style={{ height: `${(month.rfis / maxTotal) * 150}px` }} title={`RFIs: ${month.rfis}`} />
                              <div className="bg-amber-500 w-full" style={{ height: `${(month.site_instructions / maxTotal) * 150}px` }} title={`SIs: ${month.site_instructions}`} />
                              <div className="bg-red-500 w-full" style={{ height: `${(month.delay_events / maxTotal) * 150}px` }} title={`Events: ${month.delay_events}`} />
                              <div className="bg-purple-500 w-full" style={{ height: `${(month.notices / maxTotal) * 150}px` }} title={`Notices: ${month.notices}`} />
                              <div className="bg-slate-500 w-full rounded-b" style={{ height: `${(month.claims / maxTotal) * 150}px` }} title={`Claims: ${month.claims}`} />
                            </div>
                            <span className="text-xs text-slate-500 mt-2 whitespace-nowrap">{month.month}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-center gap-4 mt-4">
                      <div className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-500 rounded" /> <span className="text-xs">RFIs</span></div>
                      <div className="flex items-center gap-1"><div className="w-3 h-3 bg-amber-500 rounded" /> <span className="text-xs">SIs</span></div>
                      <div className="flex items-center gap-1"><div className="w-3 h-3 bg-red-500 rounded" /> <span className="text-xs">Events</span></div>
                      <div className="flex items-center gap-1"><div className="w-3 h-3 bg-purple-500 rounded" /> <span className="text-xs">Notices</span></div>
                      <div className="flex items-center gap-1"><div className="w-3 h-3 bg-slate-500 rounded" /> <span className="text-xs">Claims</span></div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-slate-400 py-8">No activity data available</p>
                )}
              </CardContent>
            </Card>

            {/* Status Distribution Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileQuestion className="h-4 w-4 text-blue-500" />
                    RFI Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DonutChart data={rfiChartData} colors={['#3b82f6', '#10b981', '#ef4444']} size={120} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    Delay Events
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DonutChart data={eventChartData} colors={['#ef4444', '#f59e0b', '#10b981']} size={120} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Bell className="h-4 w-4 text-purple-500" />
                    Notices
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DonutChart data={noticeChartData} colors={['#8b5cf6', '#3b82f6', '#10b981']} size={120} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-slate-500" />
                    Claims
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DonutChart data={claimChartData} colors={['#64748b', '#3b82f6', '#10b981', '#ef4444']} size={120} />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Deadlines Tab */}
          <TabsContent value="deadlines">
            <Card>
              <CardHeader>
                <CardTitle>All Upcoming Deadlines</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {upcoming_deadlines?.length > 0 ? upcoming_deadlines.map((item, i) => (
                    <div 
                      key={i}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => navigate(`/projects/${item.project_id}`)}
                    >
                      <div className="flex items-center gap-4">
                        <Badge variant="outline" className={
                          item.type === 'RFI' ? 'bg-blue-100 text-blue-700' :
                          item.type === 'Notice' ? 'bg-purple-100 text-purple-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {item.type}
                        </Badge>
                        <div>
                          <p className="font-medium">{item.reference}</p>
                          <p className="text-sm text-slate-500">{item.title}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">{item.project_name}</p>
                        <Badge className={
                          item.days_remaining <= 1 ? 'bg-red-500' :
                          item.days_remaining <= 3 ? 'bg-amber-500' :
                          'bg-green-500'
                        }>
                          {item.days_remaining === 0 ? 'Today' : 
                           item.days_remaining === 1 ? 'Tomorrow' : 
                           `${item.days_remaining} days`}
                        </Badge>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8">
                      <Clock className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">No upcoming deadlines in the next 7 days</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="space-y-6">
            {/* Billing Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Total Invoiced</p>
                  <p className="text-2xl font-bold text-slate-900">R{(billing_summary?.total_invoiced || 0).toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Total Paid</p>
                  <p className="text-2xl font-bold text-green-600">R{(billing_summary?.total_paid || 0).toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Outstanding</p>
                  <p className="text-2xl font-bold text-amber-600">R{(billing_summary?.outstanding || 0).toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Overdue Invoices</p>
                  <p className="text-2xl font-bold text-red-600">{billing_summary?.overdue_invoices || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Billing by Branch */}
            {billing_by_branch && billing_by_branch.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Billing by Branch</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {billing_by_branch.map((branch, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Building2 className="h-5 w-5 text-slate-400" />
                          <div>
                            <p className="font-medium">{branch.branch_name}</p>
                            {branch.is_head_office && <Badge variant="outline" className="text-xs">Head Office</Badge>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">R{(branch.total_invoiced || 0).toLocaleString()}</p>
                          <p className="text-sm text-amber-600">Outstanding: R{(branch.outstanding || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Generated Documents
                </CardTitle>
                <Button variant="outline" onClick={loadDocuments} disabled={documentsLoading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${documentsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {documentsLoading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin text-slate-400 mx-auto" />
                  </div>
                ) : documents.length > 0 ? (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-white rounded-lg border">
                            <FileText className="h-5 w-5 text-slate-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">{doc.document_type}</Badge>
                              <p className="font-mono font-semibold">{doc.reference_number}</p>
                            </div>
                            <p className="text-sm text-slate-600">{doc.title}</p>
                            <div className="flex gap-3 mt-1 text-xs text-slate-500">
                              <span>{doc.page_count || 0} pages</span>
                              {doc.annexure_count > 0 && <span>{doc.annexure_count} annexures</span>}
                              <span>{new Date(doc.generated_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {doc.has_pdf && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadDocument(doc.id, 'pdf')}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              PDF
                            </Button>
                          )}
                          {doc.has_docx && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadDocument(doc.id, 'docx')}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              DOCX
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500 mb-2">No documents generated yet</p>
                    <p className="text-sm text-slate-400">Documents are generated from Notices, Claims, and RFIs for this client's projects</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branches Tab */}
          <TabsContent value="branches">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Client Branches</CardTitle>
                <Button onClick={() => { setEditingBranch(null); setBranchDialogOpen(true); }}>
                  <Plus className="h-4 w-4 mr-2" /> Add Branch
                </Button>
              </CardHeader>
              <CardContent>
                {client.branches && client.branches.length > 0 ? (
                  <div className="space-y-3">
                    {client.branches.map((branch) => (
                      <div key={branch.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-white rounded-lg border">
                            <Building2 className="h-6 w-6 text-slate-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold">{branch.name}</p>
                              {branch.is_head_office && <Badge className="bg-blue-500">Head Office</Badge>}
                            </div>
                            {branch.physical_address && (
                              <p className="text-sm text-slate-500">
                                {[branch.physical_address.street, branch.physical_address.city, branch.physical_address.province].filter(Boolean).join(', ')}
                              </p>
                            )}
                            <div className="flex gap-4 mt-1 text-sm text-slate-500">
                              {branch.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {branch.phone}</span>}
                              {branch.billing_email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {branch.billing_email}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => { setEditingBranch(branch); setBranchDialogOpen(true); }}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteBranch(branch.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Building2 className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500 mb-4">No branches configured</p>
                    <Button onClick={() => { setEditingBranch(null); setBranchDialogOpen(true); }}>
                      <Plus className="h-4 w-4 mr-2" /> Add First Branch
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Branch Dialog */}
        <BranchFormDialog
          open={branchDialogOpen}
          onOpenChange={setBranchDialogOpen}
          clientId={clientId}
          branch={editingBranch}
          onSuccess={loadDashboard}
        />
      </div>
    </DashboardLayout>
  );
}
